import {
  Button,
  Col,
  Drawer,
  Row,
  List,
  Space,
  Typography,
  Collapse,
  Tooltip,
  DatePicker,
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { paymentMethods } from "../../Enums/PaymentMethod.enums";
import { salesStatus } from "../../Enums/SalesStatus.enums";
import {
  CloseOutlined,
  FileTextOutlined,
  MailOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import ProfilePermissionService from "../../services/profilePermissions.service";
import { formatCurrency } from "../../services/utils";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AlertService } from "../../services/alert.service";
import { sendInvoice, updateStatus } from "../../services/invoices.service";
import { getById } from "../../services/sales.service";
import ConfirmModal from "../../components/Commons/Modal/confirm-modal.component";
import dayjs from "dayjs";

const { Text } = Typography;

interface Details {
  details: any;
  openDetails: boolean;
  onCloseDetails: any;
}

export default function DetailSale({
  details,
  openDetails,
  onCloseDetails,
}: Details) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { data: saleResponse } = useQuery(
    ["saleDetails", details?.uuid],
    () => (details?.uuid ? getById(details.uuid) : null),
    {
      enabled: !!details?.uuid && openDetails,
      refetchOnWindowFocus: false,
    }
  );

  const currentDetails = saleResponse?.data || details;

  const [sendingInvoiceLoading, setSendingInvoiceLoading] = useState<
    string | null
  >(null);
  const [openSendInvoiceConfirm, setOpenSendInvoiceConfirm] = useState<
    string | null
  >(null);

  const [payInvoiceLoading, setPayInvoiceLoading] = useState<string | null>(
    null
  );
  const [openPayInvoiceConfirm, setOpenPayInvoiceConfirm] = useState<
    string | null
  >(null);

  const [paymentDate, setPaymentDate] = useState(dayjs());

  const { mutate: payInvoiceMutate } = useMutation(updateStatus, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Fatura quitada com sucesso.", type: "success" },
      ]);
      setPayInvoiceLoading(null);
      setOpenPayInvoiceConfirm(null);
      queryClient.invalidateQueries(["saleDetails", details?.uuid]);
    },
    onError: () => {
      AlertService.sendAlert([
        { text: "Falha ao quitar fatura.", type: "error" },
      ]);
      setPayInvoiceLoading(null);
    },
  });

  const { mutate: sendInvoiceMutate } = useMutation(sendInvoice, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Fatura enviada por e-mail com sucesso.", type: "success" },
      ]);
      setSendingInvoiceLoading(null);
    },
    onError: () => {
      AlertService.sendAlert([
        { text: "Falha ao enviar fatura por e-mail.", type: "error" },
      ]);
      setSendingInvoiceLoading(null);
    },
  });

  const invoiceStatusLabel = (status: string) => {
    if (["Paid", "Paga"].includes(status)) return "Paga";
    if (["Pending", "Pendente"].includes(status)) return "Pendente";
    if (["Canceled", "Cancelada"].includes(status)) return "Cancelada";
    return status || "";
  };

  return (
    <Drawer
      title="Detalhes"
      placement="right"
      closeIcon={false}
      width={500}
      open={openDetails}
      onClose={onCloseDetails}
      extra={
        <Button
          type="text"
          shape="circle"
          size="middle"
          icon={<CloseOutlined />}
          onClick={onCloseDetails}
        />
      }
    >
      {currentDetails && (
        <>
          <Row>
            <Col span={24} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Data</Text>
                <Text>
                  {currentDetails.date
                    ? moment(currentDetails.date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Cliente</Text>
                <Text>{currentDetails.customer.name}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Vendedor</Text>
                <Text>{currentDetails.seller.name}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Meio de Pagamento</Text>
                <Text>
                  {paymentMethods.find(
                    (method) => method.value === currentDetails.payment_method
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Desconto</Text>
                <Text>{currentDetails.discount}%</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Valor Total</Text>
                <Text>{formatCurrency(currentDetails.total_amount || 0)}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Data de Vencimento</Text>
                <Text>
                  {currentDetails.due_date
                    ? moment(currentDetails.due_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Parcelamento</Text>
                <Text>{currentDetails.installments}</Text>
              </Space>
            </Col>

            <Col span={12} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Status</Text>
                <Text>
                  {salesStatus.find(
                    (_status) => _status.value === currentDetails.status
                  )?.label || ""}
                </Text>
              </Space>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Collapse defaultActiveKey={[]}>
                <Collapse.Panel
                  header={<Text strong>Serviços</Text>}
                  key="services"
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={currentDetails.saleServices}
                    renderItem={(item: any, index) => (
                      <List.Item>
                        <List.Item.Meta
                          title={item?.service?.name || "Serviço"}
                          description={`Preço: ${formatCurrency(
                            item?.price || 0
                          )} | Quantidade: ${item?.quantity || 0}`}
                        />
                      </List.Item>
                    )}
                  />
                </Collapse.Panel>
              </Collapse>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Collapse defaultActiveKey={[]}>
                <Collapse.Panel
                  header={<Text strong>Faturas</Text>}
                  key="invoices"
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={currentDetails?.invoices || []}
                    renderItem={(invoice: any, index) => (
                      <List.Item
                        actions={[
                          <Tooltip title="Abrir fatura" key={`open_${index}`}>
                            <Button
                              type="link"
                              size="small"
                              icon={<FileTextOutlined />}
                              onClick={() =>
                                window.open(
                                  `/invoices/${invoice?.uuid}`,
                                  "_blank"
                                )
                              }
                            />
                          </Tooltip>,
                          <Tooltip title="Quitar fatura" key={`pay_${index}`}>
                            <Button
                              style={{
                                display:
                                  invoice?.status === "Paid" ? "none" : "block",
                              }}
                              type="link"
                              size="small"
                              loading={payInvoiceLoading === invoice?.uuid}
                              icon={<CheckCircleOutlined />}
                              onClick={() =>
                                setOpenPayInvoiceConfirm(invoice?.uuid)
                              }
                              disabled={
                                !["Pending", "Pendente"].includes(
                                  invoice?.status || ""
                                )
                              }
                            />
                          </Tooltip>,
                          <Tooltip
                            title="Enviar fatura por e-mail"
                            key={`send_${index}`}
                          >
                            <Button
                              style={{
                                display:
                                  invoice?.status === "Paid" ? "none" : "block",
                              }}
                              type="link"
                              size="small"
                              loading={sendingInvoiceLoading === invoice?.uuid}
                              icon={<MailOutlined />}
                              onClick={() =>
                                setOpenSendInvoiceConfirm(invoice?.uuid)
                              }
                              disabled={
                                !["Pending", "Pendente"].includes(
                                  invoice?.status || ""
                                ) ||
                                (["Pending", "Pendente"].includes(
                                  invoice?.status || ""
                                ) &&
                                  invoice?.due_date &&
                                  moment(
                                    invoice?.due_date,
                                    "YYYY-MM-DD"
                                  ).isBefore(moment(), "day"))
                              }
                            />
                          </Tooltip>,
                        ]}
                      >
                        <List.Item.Meta
                          title={`Fatura ${
                            invoice?.number || invoice?.id || ""
                          }`}
                          description={`Vencimento: ${
                            invoice?.due_date
                              ? moment(invoice?.due_date, "YYYY-MM-DD").format(
                                  "DD/MM/YYYY"
                                )
                              : ""
                          } | Valor: ${formatCurrency(
                            parseFloat(invoice?.total_amount || 0)
                          )} | Situação: ${
                            ["Pending", "Pendente"].includes(
                              invoice?.status || ""
                            ) &&
                            invoice?.due_date &&
                            moment(invoice?.due_date, "YYYY-MM-DD").isBefore(
                              moment(),
                              "day"
                            )
                              ? "Vencida"
                              : invoiceStatusLabel(invoice?.status || "")
                          }`}
                        />
                      </List.Item>
                    )}
                  />
                </Collapse.Panel>
              </Collapse>
            </Col>

            <Col span={24} style={{ marginBottom: 15 }}>
              <Space direction="vertical">
                <Text strong>Discriminação</Text>
                <Text>{currentDetails.description}</Text>
              </Space>
            </Col>

            {profilePermissionService.hasPermission(`sales-sales:write`) && (
              <Col span={24} style={{ marginBottom: 15 }}>
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 20 }}
                  onClick={() => {
                    navigate(`/sales/${currentDetails.uuid}/edit`);
                    onCloseDetails();
                  }}
                >
                  Editar
                </Button>
              </Col>
            )}
          </Row>
          <ConfirmModal
            open={!!openSendInvoiceConfirm}
            title="Deseja mesmo enviar a fatura por-email agora?"
            okText="Sim, enviar"
            cancelText="Cancelar"
            onOk={() => {
              const uuid = openSendInvoiceConfirm as string;
              setSendingInvoiceLoading(uuid);
              sendInvoiceMutate({ values: {}, uuid });
              setOpenSendInvoiceConfirm(null);
            }}
            onCancel={() => setOpenSendInvoiceConfirm(null)}
          />
          <ConfirmModal
            open={!!openPayInvoiceConfirm}
            loading={payInvoiceLoading === openPayInvoiceConfirm}
            title="Deseja mesmo quitar esta fatura agora?"
            okText="Sim, quitar"
            cancelText="Cancelar"
            onOk={() => {
              const uuid = openPayInvoiceConfirm as string;
              setPayInvoiceLoading(uuid);
              payInvoiceMutate({
                values: {
                  status: "Paid",
                  paid_at: paymentDate.format("YYYY-MM-DD"),
                },
                uuid,
              });
            }}
            onCancel={() => setOpenPayInvoiceConfirm(null)}
          >
            <div style={{ marginLeft: 31, marginTop: 10 }}>
              <Text strong>Data do Pagamento</Text>
              <DatePicker
                style={{ width: "100%", marginTop: 5 }}
                format="DD/MM/YYYY"
                allowClear={false}
                value={paymentDate}
                onChange={(date) => setPaymentDate(date || dayjs())}
              />
            </div>
          </ConfirmModal>
        </>
      )}
    </Drawer>
  );
}
